import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  Autocomplete,
  Button,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import { NotificationContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useCreateAppointmentMutation,
  useGetShopResourcesQuery,
  useUpdateAppointmentMutation,
  useUpdateRepairOrderMutation
} from '../../redux/api'
import { useCreateOneTimeReminderMutation } from '../../redux/api/reminder'
import {
  AppointmentInfo,
  CustomerEntry,
  OneTimeReminderInfoTypeEnum,
  OneTimeReminderInfoViaEnum,
  RepairOrderExpanded,
  VehicleEntry
} from '../../sdk'
import { defaultTimeZoneDateToUtcDate } from '../../utils'
import { AppointmentColorSelect } from '../Appointments/AppointmentColorSelect'
import { CustomerLookupInput } from '../CustomerLookUp'
import {
  ControlledMuiCheckboxSingle,
  ControlledMuiTextField
} from '../FormField'
import {
  AppointmentReminderFields,
  AppointmentReminderFieldsType
} from '../Reminder/AppointmentReminderFields'
import { CardUI } from '../SharedUI'
import { DatePickerWithAlert } from '../SharedUI/DatePickerWithAlert'
import { VehiclesByCustomerAutocomplete } from '../VehiclesByCustomerAutocomplete/VehiclesByCustomerAutocomplete'
import {
  getAppointmentReminderMessage,
  getAppointmentReminderTitle
} from './getDefaultEmail'
import { NEW_APPOINTMENT_VALIDATION } from './validation'

export interface AppointmentFormInputs {
  notes: string
  sendText: boolean
  sendEmail: boolean
  vip: boolean
  waiting: boolean
  concern: string
  estimatedWorkHours: number
  resources: Array<string>
}

interface Props {
  appointment?: SerializableAppointmentEntry | undefined
  vehicle: VehicleEntry | undefined
  customer: CustomerEntry | undefined
  repairOrder: RepairOrderExpanded | undefined
  date: Date
  color: string
  resources: string[]
  cannedJobs: string[]
  services: string[]
  submitCallBack: () => void
  cancelCallBack: () => void
  // use props below if updates in the form needs to be reflected in other components
  setDateOutside?: (date: Date) => void
  setResourceOutside?: (resource: string[]) => void
  setCustomerOutside?: (c: CustomerEntry | undefined) => void
  setVehicleOutside?: (v: VehicleEntry | undefined) => void
  setColorOutside?: (c: string) => void
}
export const AppointmentForm: FC<Props> = ({
  appointment,
  vehicle,
  customer,
  submitCallBack,
  cancelCallBack,
  date,
  color,
  resources,
  cannedJobs,
  services,
  repairOrder,
  setDateOutside,
  setResourceOutside,
  setCustomerOutside,
  setVehicleOutside,
  setColorOutside
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { setNotice } = useContext(NotificationContext)
  const { shopProfile } = useContext(AppContext)
  const { data: shopResources } = useGetShopResourcesQuery({
    authorization: accessToken,
    shop: shopId
  })

  const [updateAppointment] = useUpdateAppointmentMutation()
  const [createAppointment] = useCreateAppointmentMutation()
  const [updateRepairOrder] = useUpdateRepairOrderMutation()
  const [createOneTimeReminder] = useCreateOneTimeReminderMutation()

  const shopResourcesAll = useMemo(() => {
    if (!shopResources) {
      return []
    }
    return [
      ...shopResources,
      { id: 'Unassigned', info: { name: 'Unassigned' } }
    ]
  }, [shopResources])

  const [newDate, setNewDate] = useState<Dayjs | null>(dayjs(date))
  const [newResource, setNewResource] = useState<string[]>(resources)
  const [newCustomer, setNewCustomer] = useState(customer)
  const [newVehicle, setNewVehicle] = useState(vehicle)
  const [noCustomerError, setNoCustomerError] = useState(false)
  const [newColor, setNewColor] = useState(color)

  useEffect(() => {
    setNewDate(dayjs(date))
    setNewResource(resources)
    setNewCustomer(customer)
    setNewVehicle(vehicle)
    setNewColor(color)
  }, [appointment?.info, customer, date, resources, vehicle, color])

  const defaultValues = useMemo(() => {
    if (!appointment) {
      return {
        notes: '',
        sendText: false,
        sendEmail: false,
        vip: false,
        waiting: false,
        concern: repairOrder?.entry?.info?.concern || '',
        estimatedWorkHours: 1,
        resources,
        cannedJobs,
        services,
        isConfirmed: true
      }
    }
    return {
      ...appointment!.info!,
      sendText: false,
      sendEmail: false,
      // non active resources should be filtered out
      resources,
      cannedJobs,
      services
    }
  }, [
    appointment,
    cannedJobs,
    repairOrder?.entry?.info?.concern,
    resources,
    services
  ])
  // set initial values for reminder form
  const [appointmentReminderFields, setAppointmentReminderFields] =
    useState<AppointmentReminderFieldsType>({
      vias: newCustomer?.information?.phone
        ? [OneTimeReminderInfoViaEnum.Sms]
        : newCustomer?.information?.email
        ? [OneTimeReminderInfoViaEnum.Email]
        : [],
      title: getAppointmentReminderTitle(shopProfile!, newDate?.toDate()),
      message: getAppointmentReminderMessage(
        shopProfile!,
        newCustomer,
        newDate?.toDate()
      ),
      scheduledDateTimes: [],
      customDateTime: newDate?.toDate() || new Date()
    })
  // update title and message when customer or date changes
  useEffect(() => {
    setAppointmentReminderFields((prev) => ({
      ...prev,
      title: getAppointmentReminderTitle(shopProfile!, newDate?.toDate()),
      message: getAppointmentReminderMessage(
        shopProfile!,
        newCustomer,
        newDate?.toDate()
      )
    }))
  }, [newCustomer, newDate, shopProfile])
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<AppointmentFormInputs>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(NEW_APPOINTMENT_VALIDATION)
  })
  const handleResourceChange = useCallback(
    (res: string[]) => {
      setNewResource(res)
      if (setResourceOutside) {
        setResourceOutside(res)
      }
    },
    [setResourceOutside]
  )
  const handleCustomerChange = useCallback(
    (customer: CustomerEntry | undefined) => {
      setNewCustomer(customer)
      setNewVehicle(undefined)
      if (setCustomerOutside) {
        setCustomerOutside(customer)
      }
      if (setVehicleOutside) {
        setVehicleOutside(undefined)
      }
    },
    [setCustomerOutside, setVehicleOutside]
  )
  const handleVehicleChange = useCallback(
    (vehicle: VehicleEntry | undefined) => {
      setNewVehicle(vehicle)
      if (setVehicleOutside) {
        setVehicleOutside(vehicle)
      }
    },
    [setVehicleOutside]
  )
  const submitHandler: () => SubmitHandler<AppointmentFormInputs> = useCallback(
    () => async (data) => {
      if (!newDate) {
        return
      }

      const { sendEmail, sendText, ...infoForExistingAppt } = data

      if (!newCustomer) {
        setNoCustomerError(true)
        return
      }

      const info: AppointmentInfo = {
        ...infoForExistingAppt,
        shop: shopId,
        customer: newCustomer!.id,
        vehicle: newVehicle?.id || undefined,
        startDateTime: defaultTimeZoneDateToUtcDate(newDate?.toDate()),
        resources: newResource.filter((id) => id !== 'Unassigned'),
        color: newColor
      }

      if (appointment) {
        await updateAppointment({
          authorization: accessToken,
          appointmentEntry: { id: appointment.id, info }
        })
      } else {
        const createdId = await createAppointment({
          authorization: accessToken,
          createAppointmentRequest: {
            info,
            sendEmailConfirmation: sendEmail,
            sendTextConfirmation: sendText
          }
        }).unwrap()
        // send reminders if set
        if (
          appointmentReminderFields.scheduledDateTimes.length > 0 &&
          appointmentReminderFields.vias.length > 0
        ) {
          for (const time of appointmentReminderFields.scheduledDateTimes) {
            let scheduledDateTime
            // if time is immediate, scheduledDateTime is undefined
            if (time === 'custom') {
              scheduledDateTime = appointmentReminderFields.customDateTime
            }
            if (time === 'hour') {
              scheduledDateTime = newDate.add(-1, 'hour').toDate()
            }
            if (time === 'day') {
              scheduledDateTime = newDate.add(-1, 'day').toDate()
            }
            if (time === 'week') {
              scheduledDateTime = newDate.add(-1, 'week').toDate()
            }
            for (const via of appointmentReminderFields.vias) {
              createOneTimeReminder({
                authorization: accessToken,
                oneTimeReminderInfo: {
                  type: OneTimeReminderInfoTypeEnum.Appointment,
                  customer: newCustomer.id!,
                  phoneNumber:
                    via === OneTimeReminderInfoViaEnum.Sms
                      ? newCustomer.information?.phone
                      : undefined,
                  email:
                    via === OneTimeReminderInfoViaEnum.Email
                      ? newCustomer.information?.email
                      : undefined,
                  message: appointmentReminderFields.message,
                  title:
                    via === OneTimeReminderInfoViaEnum.Email
                      ? appointmentReminderFields.title
                      : undefined,
                  scheduledDateTime,
                  via
                }
              })
            }
          }
        }

        if (repairOrder) {
          await updateRepairOrder({
            authorization: accessToken,
            repairOrderEntry: {
              ...repairOrder.entry!,
              info: {
                ...repairOrder.entry!.info!,
                appointment: createdId
              }
            }
          })
        }
      }

      setNotice('The appointment has been updated.')
      if (submitCallBack) {
        await submitCallBack()
      }
    },
    [
      accessToken,
      appointment,
      appointmentReminderFields.customDateTime,
      appointmentReminderFields.message,
      appointmentReminderFields.scheduledDateTimes,
      appointmentReminderFields.title,
      appointmentReminderFields.vias,
      createAppointment,
      createOneTimeReminder,
      newColor,
      newCustomer,
      newDate,
      newResource,
      newVehicle?.id,
      repairOrder,
      setNotice,
      shopId,
      submitCallBack,
      updateAppointment,
      updateRepairOrder
    ]
  )
  const Heading = useMemo(
    () => (
      <Typography variant="h2">
        {appointment ? 'Edit Appointment' : 'New Appointment'}
      </Typography>
    ),
    [appointment]
  )

  const ActionsRight = useMemo(
    () => (
      <Stack direction="row" spacing={1}>
        <IconButton
          size="small"
          onClick={() => {
            cancelCallBack()
          }}>
          <Close />
        </IconButton>
      </Stack>
    ),
    [cancelCallBack]
  )

  return (
    <CardUI
      heading={Heading}
      actionsRight={ActionsRight}
      sx={{
        minWidth: '270px',
        maxWidth: '500px'
      }}
      aria-label="appointmentForm">
      <Stack spacing={2}>
        <Stack spacing={2}>
          <DatePickerWithAlert
            label={'Appointment Date'}
            alertCondition={!date}
            alertText={'No date'}
            value={newDate}
            onChange={(newDate) => {
              setNewDate(newDate)
              if (newDate && setDateOutside) {
                setDateOutside(newDate.toDate())
              }
            }}
            canClear={false}
            withTime={true}
            sx={{ width: '100%' }}
          />
          <div>
            <CustomerLookupInput
              customer={newCustomer}
              handleCustomerChange={handleCustomerChange}
              error={noCustomerError}
            />
            {noCustomerError && (
              <FormHelperText error={true}>
                Please select a customer for the appointment
              </FormHelperText>
            )}
          </div>
          {newCustomer && (
            <VehiclesByCustomerAutocomplete
              customer={newCustomer}
              vehicle={newVehicle}
              setVehicle={handleVehicleChange}
              setCustomer={handleCustomerChange}
            />
          )}
          {repairOrder && (
            <Typography>
              Repair Order #: {repairOrder.entry!.localId!}
            </Typography>
          )}
        </Stack>
        <form onSubmit={handleSubmit(submitHandler())}>
          <Stack spacing={2}>
            <ControlledMuiTextField
              control={control}
              name="concern"
              aria-label="formConcernField"
              required
              id="concern"
              label="Concern"
              fullWidth
            />
            <ControlledMuiTextField
              control={control}
              name="estimatedWorkHours"
              aria-label="formWorkHoursField"
              id="estimatedWorkHours"
              label="Estimated Work Hours"
              fullWidth
            />
            {shopResources && (
              <Autocomplete
                multiple
                limitTags={3}
                id="resources"
                aria-label="formResourceField"
                options={shopResourcesAll.map((resource) => resource.id!)}
                getOptionLabel={(option) => {
                  return shopResourcesAll.filter(
                    (resource) => resource.id === option
                  )[0].info!.name!
                }}
                disableCloseOnSelect
                value={newResource}
                onChange={(e, data: string[]) => {
                  handleResourceChange(data)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Resources"
                    placeholder="Resources"
                  />
                )}
              />
            )}
            <ControlledMuiTextField
              control={control}
              name="notes"
              aria-label="formNotesField"
              multiline
              id="appointmentNotes"
              label="Notes (Private)"
              rows={4}
              fullWidth
            />
            <Stack spacing={1}>
              {!appointment && (
                <ControlledMuiCheckboxSingle
                  control={control}
                  name="sendText"
                  aria-label="formSendTextCheckbox"
                  label={'Send Text Confirmation'}
                  disabled={!customer?.information?.phone}
                  helperText={
                    !customer?.information?.phone
                      ? 'No phone number found for the customer'
                      : ''
                  }
                />
              )}
              {!appointment && (
                <ControlledMuiCheckboxSingle
                  control={control}
                  name="sendEmail"
                  aria-label="formSendEmailCheckbox"
                  label="Send Email Confirmation"
                  disabled={!customer?.information?.email}
                  helperText={
                    !customer?.information?.email
                      ? 'No email found for the customer'
                      : ''
                  }
                />
              )}
              <Stack direction={'row'} alignItems="center" spacing={1}>
                <ControlledMuiCheckboxSingle
                  control={control}
                  name="vip"
                  aria-label="formVipCheckbox"
                  label="VIP"
                />
                <ControlledMuiCheckboxSingle
                  control={control}
                  name="waiting"
                  aria-label="formWaitingCheckbox"
                  label="Waiting"
                />
                <Stack direction="row" alignItems={'center'}>
                  <Typography variant="subtitle1" component="span">
                    Color:&nbsp;
                  </Typography>
                  <AppointmentColorSelect
                    color={newColor}
                    setColor={(color) => {
                      setNewColor(color)
                      if (setColorOutside) {
                        setColorOutside(color)
                      }
                    }}
                  />
                </Stack>
              </Stack>
              {!appointment &&
                newDate &&
                newDate.toDate().getTime() > new Date().getTime() && (
                  <AppointmentReminderFields
                    {...{
                      appointmentReminderFields,
                      setAppointmentReminderFields
                    }}
                    appointmentTime={newDate.toDate()}
                    customer={newCustomer}
                  />
                )}
            </Stack>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !newDate}>
              {isSubmitting ? 'Sending...' : 'Submit'}
            </Button>
          </Stack>
        </form>
      </Stack>
    </CardUI>
  )
}
